import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Pressable, ScrollView, Animated, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const PollResults = ({ polls, formatDate, cardStyle, isMobile }) => {
    const [expandedPolls, setExpandedPolls] = useState({});

    // Animation values
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const slideAnim = useRef(new Animated.Value(20)).current;
    const scaleAnim = useRef(new Animated.Value(0.95)).current;

    // Entrance animations
    useEffect(() => {
        Animated.parallel([
            Animated.timing(fadeAnim, {
                toValue: 1,
                duration: 400,
                useNativeDriver: true
            }),
            Animated.timing(slideAnim, {
                toValue: 0,
                duration: 400,
                useNativeDriver: true
            }),
            Animated.spring(scaleAnim, {
                toValue: 1,
                friction: 8,
                tension: 40,
                useNativeDriver: true
            })
        ]).start();
    }, []);

    const togglePoll = (pollId) => {
        setExpandedPolls(prev => ({
            ...prev,
            [pollId]: !prev[pollId]
        }));
    };

    const colors = {
        primary: '#4c956c',
        orange: '#f97316',
        text: '#1a3a2a',
        textMuted: '#5a6b63',
    };

    if (!polls || polls.length === 0) {
        return (
            <Animated.View style={[
                cardStyle, 
                { 
                    padding: isMobile ? 20 : 28, 
                    overflow: 'hidden', 
                    position: 'relative',
                    opacity: fadeAnim,
                    transform: [
                        { translateY: slideAnim },
                        { scale: scaleAnim }
                    ]
                }
            ]}>
                {/* Decorative Bubbles */}
                <Animated.View style={{
                    position: 'absolute',
                    top: -15,
                    left: -15,
                    width: 60,
                    height: 60,
                    borderRadius: 30,
                    backgroundColor: 'rgba(249, 115, 22, 0.06)',
                    opacity: fadeAnim
                }} />
                
                {/* Header */}
                <View style={{ marginBottom: 20 }}>
                    <Text style={{
                        fontSize: isMobile ? 18 : 22,
                        fontWeight: '700',
                        color: colors.text,
                        fontFamily: 'Bodoni72Smallcaps'
                    }}>
                        Poll Results
                    </Text>
                    <Text style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
                        See how people voted
                    </Text>
                </View>
                
                <View style={{
                    padding: 40,
                    alignItems: 'center',
                    backgroundColor: 'rgba(249, 115, 22, 0.08)',
                    borderRadius: 16,
                    borderWidth: 2,
                    borderColor: 'rgba(249, 115, 22, 0.15)',
                    borderStyle: 'dashed'
                }}>
                    <View style={{
                        width: 64,
                        height: 64,
                        borderRadius: 32,
                        backgroundColor: 'rgba(249, 115, 22, 0.15)',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 16
                    }}>
                        <Ionicons name="bar-chart-outline" size={32} color={colors.orange} />
                    </View>
                    <Text style={{ fontSize: 18, fontWeight: 'bold', color: colors.text, marginBottom: 8 }}>
                        No polls yet
                    </Text>
                    <Text style={{ fontSize: 14, color: colors.textMuted, textAlign: 'center' }}>
                        Create a poll on your announcements page to see results here
                    </Text>
                </View>
            </Animated.View>
        );
    }

    return (
        <Animated.View style={[
            cardStyle, 
            { 
                padding: isMobile ? 20 : 28, 
                overflow: 'hidden', 
                position: 'relative',
                opacity: fadeAnim,
                transform: [
                    { translateY: slideAnim },
                    { scale: scaleAnim }
                ]
            }
        ]}>
            {/* Decorative Bubbles */}
            <Animated.View style={{
                position: 'absolute',
                top: -15,
                left: -15,
                width: 60,
                height: 60,
                borderRadius: 30,
                backgroundColor: 'rgba(249, 115, 22, 0.06)',
                opacity: fadeAnim
            }} />
            <Animated.View style={{
                position: 'absolute',
                bottom: 40,
                right: -20,
                width: 70,
                height: 70,
                borderRadius: 35,
                backgroundColor: 'rgba(249, 115, 22, 0.04)'
            }} />
            
            {/* Header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <View>
                    <Text style={{
                        fontSize: isMobile ? 18 : 22,
                        fontWeight: '700',
                        color: colors.text,
                        fontFamily: 'Bodoni72Smallcaps'
                    }}>
                        Poll Results
                    </Text>
                    <Text style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
                        See how people voted
                    </Text>
                </View>
                
                <View style={{
                    backgroundColor: colors.orange,
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 16
                }}>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>
                        {polls.length}
                    </Text>
                </View>
            </View>

            {polls.map((poll, index) => {
                const isExpanded = expandedPolls[poll.id];
                const totalVotes = poll.votes ? poll.votes.length : 0;
                
                // Check if poll data is incomplete
                const hasQuestion = poll.question && poll.question.trim() !== '';
                const hasAnswers = poll.answers && Array.isArray(poll.answers) && poll.answers.length > 0;
                const isIncomplete = !hasQuestion || !hasAnswers;
                
                // Calculate vote counts per answer
                // Answers are stored as: [{ id, text, isCorrect }]
                // Votes are stored as: [{ userId, answerId, timestamp }]
                const votesByAnswer = {};
                if (poll.answers && Array.isArray(poll.answers)) {
                    poll.answers.forEach((answer) => {
                        const answerId = answer.id || answer;
                        votesByAnswer[answerId] = poll.votes ? poll.votes.filter(v => v.answerId === answerId) : [];
                    });
                }

                return (
                    <View
                        key={poll.id || index}
                        style={{
                            backgroundColor: '#f5f0e1',
                            borderRadius: 12,
                            padding: isMobile ? 14 : 18,
                            marginBottom: 12,
                            borderWidth: 1,
                            borderColor: 'rgba(76, 149, 108, 0.15)'
                        }}
                    >
                        <Pressable
                            onPress={() => togglePoll(poll.id)}
                            style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}
                        >
                            <View style={{ flex: 1, marginRight: 12 }}>
                                <Text style={{
                                    fontSize: isMobile ? 16 : 18,
                                    fontWeight: '600',
                                    color: '#1a3a2a',
                                    marginBottom: 8
                                }}>
                                    {hasQuestion ? poll.question : 'Untitled Poll (Incomplete)'}
                                </Text>
                                {isIncomplete && (
                                    <View style={{
                                        backgroundColor: '#fff3cd',
                                        borderRadius: 6,
                                        paddingHorizontal: 8,
                                        paddingVertical: 4,
                                        marginBottom: 8,
                                        alignSelf: 'flex-start'
                                    }}>
                                        <Text style={{
                                            fontSize: 11,
                                            color: '#856404',
                                            fontWeight: '600'
                                        }}>
                                            ⚠️ Poll data incomplete - edit to fix
                                        </Text>
                                    </View>
                                )}
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                    <Text style={{
                                        fontSize: 13,
                                        color: '#5a6b63',
                                        fontWeight: '500'
                                    }}>
                                        {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
                                    </Text>
                                    <Text style={{
                                        fontSize: 13,
                                        color: '#5a6b63'
                                    }}>
                                        {poll.answers?.filter(a => a.text && a.text.trim()).length || 0} options
                                    </Text>
                                </View>
                            </View>
                            <Ionicons
                                name={isExpanded ? "chevron-up" : "chevron-down"}
                                size={24}
                                color="#4c956c"
                            />
                        </Pressable>

                        {isExpanded && (
                            <View style={{ marginTop: 16 }}>
                                {isIncomplete ? (
                                    <View style={{
                                        backgroundColor: '#fff',
                                        borderRadius: 10,
                                        padding: 16,
                                        borderWidth: 1,
                                        borderColor: '#ffc107',
                                        alignItems: 'center'
                                    }}>
                                        <Ionicons name="warning" size={32} color="#ffc107" style={{ marginBottom: 8 }} />
                                        <Text style={{
                                            fontSize: 15,
                                            fontWeight: '600',
                                            color: '#1a3a2a',
                                            marginBottom: 4,
                                            textAlign: 'center'
                                        }}>
                                            Poll Data Incomplete
                                        </Text>
                                        <Text style={{
                                            fontSize: 13,
                                            color: '#5a6b63',
                                            textAlign: 'center',
                                            lineHeight: 18
                                        }}>
                                            This poll is missing {!hasQuestion ? 'a question' : ''}{!hasQuestion && !hasAnswers ? ' and ' : ''}{!hasAnswers ? 'answer options' : ''}. Please edit the poll on your Events page to complete it.
                                        </Text>
                                        {totalVotes > 0 && (
                                            <Text style={{
                                                fontSize: 12,
                                                color: '#856404',
                                                marginTop: 8,
                                                fontStyle: 'italic'
                                            }}>
                                                Note: {totalVotes} {totalVotes === 1 ? 'vote was' : 'votes were'} cast but can't be displayed
                                            </Text>
                                        )}
                                    </View>
                                ) : poll.answers && poll.answers.length > 0 ? (
                                    poll.answers.map((answer) => {
                                        // Handle both object format { id, text } and string format
                                        const answerId = answer.id || answer;
                                        const answerText = answer.text || answer;
                                        const isCorrect = answer.isCorrect || false;
                                        const answerVotes = votesByAnswer[answerId] || [];
                                        const percentage = totalVotes > 0 
                                            ? Math.round((answerVotes.length / totalVotes) * 100) 
                                            : 0;

                                        // Skip empty answers
                                        if (!answerText || answerText.trim() === '') {
                                            return null;
                                        }

                                        return (
                                            <View
                                                key={answerId}
                                                style={{
                                                    backgroundColor: '#fff',
                                                    borderRadius: 10,
                                                    padding: isMobile ? 12 : 14,
                                                    marginBottom: 10,
                                                    borderWidth: 1,
                                                    borderColor: isCorrect ? 'rgba(76, 149, 108, 0.3)' : 'rgba(76, 149, 108, 0.1)'
                                                }}
                                            >
                                                <View style={{
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: 8
                                                }}>
                                                    <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
                                                        <Text style={{
                                                            fontSize: isMobile ? 14 : 15,
                                                            fontWeight: '600',
                                                            color: '#1a3a2a',
                                                            flex: 1
                                                        }}>
                                                            {answerText}
                                                        </Text>
                                                        {isCorrect && (
                                                            <View style={{
                                                                backgroundColor: '#4c956c',
                                                                borderRadius: 6,
                                                                paddingHorizontal: 6,
                                                                paddingVertical: 2,
                                                                marginRight: 8
                                                            }}>
                                                                <Text style={{
                                                                    color: '#fff',
                                                                    fontSize: 11,
                                                                    fontWeight: '600'
                                                                }}>
                                                                    ✓ Correct
                                                                </Text>
                                                            </View>
                                                        )}
                                                    </View>
                                                    <View style={{
                                                        backgroundColor: '#4c956c',
                                                        borderRadius: 8,
                                                        paddingHorizontal: 8,
                                                        paddingVertical: 3,
                                                        marginLeft: 10
                                                    }}>
                                                        <Text style={{
                                                            color: '#fff',
                                                            fontSize: 12,
                                                            fontWeight: '600'
                                                        }}>
                                                            {percentage}%
                                                        </Text>
                                                    </View>
                                                </View>

                                                {/* Progress bar */}
                                                <View style={{
                                                    height: 6,
                                                    backgroundColor: '#e8f0e5',
                                                    borderRadius: 3,
                                                    marginBottom: 8,
                                                    overflow: 'hidden'
                                                }}>
                                                    <View style={{
                                                        height: '100%',
                                                        width: `${percentage}%`,
                                                        backgroundColor: '#4c956c',
                                                        borderRadius: 3
                                                    }} />
                                                </View>

                                                {/* Voters list */}
                                                {answerVotes.length > 0 && (
                                                    <View style={{ marginTop: 6 }}>
                                                        <Text style={{
                                                            fontSize: 12,
                                                            color: '#5a6b63',
                                                            marginBottom: 4,
                                                            fontWeight: '600'
                                                        }}>
                                                            Voted by ({answerVotes.length}):
                                                        </Text>
                                                        <View style={{
                                                            flexDirection: 'row',
                                                            flexWrap: 'wrap',
                                                            gap: 6
                                                        }}>
                                                            {answerVotes.map((vote, voteIdx) => {
                                                                // Format display name
                                                                const getDisplayName = () => {
                                                                    // If we have userName already formatted
                                                                    if (vote.userName) return vote.userName;
                                                                    
                                                                    // If it's a session/guest user
                                                                    if (vote.userId && vote.userId.startsWith('session-')) {
                                                                        return 'Guest';
                                                                    }
                                                                    
                                                                    // If we have email, show first part
                                                                    if (vote.userEmail) {
                                                                        const emailName = vote.userEmail.split('@')[0];
                                                                        // Capitalize first letter
                                                                        return emailName.charAt(0).toUpperCase() + emailName.slice(1);
                                                                    }
                                                                    
                                                                    // Fallback - don't show raw UID, show "Voter"
                                                                    return 'Voter';
                                                                };
                                                                
                                                                return (
                                                                    <View
                                                                        key={voteIdx}
                                                                        style={{
                                                                            backgroundColor: '#e8f0e5',
                                                                            borderRadius: 6,
                                                                            paddingHorizontal: 8,
                                                                            paddingVertical: 4
                                                                        }}
                                                                    >
                                                                        <Text style={{
                                                                            fontSize: 12,
                                                                            color: '#1a3a2a'
                                                                        }}>
                                                                            {getDisplayName()}
                                                                        </Text>
                                                                    </View>
                                                                );
                                                            })}
                                                        </View>
                                                    </View>
                                                )}

                                                {answerVotes.length === 0 && (
                                                    <Text style={{
                                                        fontSize: 12,
                                                        color: '#999',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        No votes yet
                                                    </Text>
                                                )}
                                            </View>
                                        );
                                    })
                                ) : (
                                    <Text style={{
                                        fontSize: 14,
                                        color: '#5a6b63',
                                        textAlign: 'center',
                                        padding: 10
                                    }}>
                                        No answer options available
                                    </Text>
                                )}
                            </View>
                        )}
                    </View>
                );
            })}
        </Animated.View>
    );
};

export default PollResults;
